import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bookmark, Plus, Search, Edit3, Trash2, Save, X, ShieldCheck, Upload, Download,
  CheckCircle2, XCircle, Loader2, ToggleLeft, ToggleRight,
} from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import LexiconTestTranslation from "./LexiconTestTranslation";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * StoicOs Lexicon — Founder Portal page.
 *
 * The Lexicon is the single source of truth for every AI workflow that
 * generates text about StoicOs (translation, AutoSEO, Holo AI marketing,
 * Founder Portal AI agents, email/social/SEO generation, future agents).
 *
 * Editing here immediately affects the next translation call — the
 * backend `lexicon_service` invalidates its cache on every write.
 */

const CATEGORY_OPTIONS = [
  { key: "brand",      label: "Brand" },
  { key: "philosophy", label: "Philosophy" },
  { key: "historical", label: "Historical" },
];

const EMPTY_FORM = {
  id: null,
  term: "",
  category: "brand",
  aliases: "",   // comma-separated in the UI, split before send
  enabled: true,
  notes: "",
};

function CategoryPill({ category }) {
  const color = category === "brand"
    ? "border-gold/40 text-gold bg-gold/5"
    : category === "philosophy"
    ? "border-purple-300/40 text-purple-200 bg-purple-400/5"
    : "border-emerald-400/40 text-emerald-300 bg-emerald-400/5";
  return (
    <span className={`inline-flex items-center border px-1.5 py-0.5 text-[9px] tracking-[0.24em] uppercase ${color}`}>
      {category}
    </span>
  );
}

export default function AdminLexicon() {
  const { t } = useT();
  const fileInputRef = useRef(null);

  const [terms, setTerms] = useState([]);
  const [counts, setCounts] = useState({ total: 0, enabled: 0, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [category, setCategory] = useState("all"); // all | brand | philosophy | historical
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | EMPTY_FORM (opens editor)

  const load = async () => {
    setErr("");
    try {
      const params = {};
      if (category !== "all") params.category = category;
      if (search.trim()) params.search = search.trim();
      const data = await adminApi.listLexicon(params);
      setTerms(data.terms || []);
      setCounts(data.counts || { total: 0, enabled: 0, by_category: {} });
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [category]);
  // debounce search
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openNew = () => setEditing({ ...EMPTY_FORM });
  const openEdit = (row) => setEditing({
    id: row.id,
    term: row.term,
    category: row.category,
    aliases: (row.aliases || []).join(", "),
    enabled: !!row.enabled,
    notes: row.notes || "",
  });
  const closeEditor = () => { setEditing(null); setMsg(""); };

  const save = async () => {
    if (!editing) return;
    if (!editing.term.trim()) { setErr(t("admin.lexicon.err.termRequired")); return; }
    setBusy(true); setErr(""); setMsg("");
    const payload = {
      term: editing.term.trim(),
      category: editing.category,
      aliases: editing.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      enabled: editing.enabled,
      notes: editing.notes,
    };
    try {
      if (editing.id) {
        await adminApi.updateLexicon(editing.id, payload);
        setMsg(t("admin.lexicon.msg.saved"));
      } else {
        await adminApi.createLexicon(payload);
        setMsg(t("admin.lexicon.msg.created"));
      }
      closeEditor();
      await load();
    } catch (e) {
      setErr(e.message || String(e));
    } finally { setBusy(false); }
  };

  const toggle = async (row) => {
    setBusy(true); setErr("");
    try {
      await adminApi.updateLexicon(row.id, { enabled: !row.enabled });
      await load();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(t("admin.lexicon.confirm.delete", { term: row.term }))) return;
    setBusy(true); setErr("");
    try {
      await adminApi.deleteLexicon(row.id);
      await load();
      setMsg(t("admin.lexicon.msg.deleted", { term: row.term }));
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const exportJson = async () => {
    setBusy(true); setErr("");
    try {
      const data = await adminApi.exportLexicon();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `stoicos-lexicon-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      setMsg(t("admin.lexicon.msg.exported", { n: data.count }));
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const importJson = async (file) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const items = Array.isArray(parsed) ? parsed : (parsed.terms || []);
      if (!items.length) throw new Error("No terms found in file");
      const res = await adminApi.importLexicon(items);
      setMsg(t("admin.lexicon.msg.imported", res));
      await load();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const filtered = terms;  // server-side filtering already applied

  return (
    <AdminShell active="lexicon">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] tracking-[0.32em] uppercase text-gold">{t("admin.lexicon.eyebrow")}</p>
            <h1 className="font-serif text-3xl md:text-4xl text-bone mt-2 flex items-center gap-3" data-testid="admin-lexicon-title">
              <Bookmark size={22} strokeWidth={1.5} className="text-gold" />
              {t("admin.lexicon.title")}
            </h1>
            <p className="mt-2 text-sm text-bone/65 max-w-2xl leading-relaxed">
              {t("admin.lexicon.subtitle")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={openNew} data-testid="admin-lexicon-new" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-gold border border-gold/60 px-3 py-1.5 hover:bg-gold/10">
              <Plus size={12} strokeWidth={1.75} /> {t("admin.lexicon.action.new")}
            </button>
            <button type="button" onClick={exportJson} disabled={busy} data-testid="admin-lexicon-export" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-bone/70 border border-white/15 px-3 py-1.5 hover:text-gold hover:border-gold/40">
              <Download size={12} strokeWidth={1.75} /> {t("admin.lexicon.action.export")}
            </button>
            <input ref={fileInputRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => { if (e.target.files?.[0]) importJson(e.target.files[0]); e.target.value = ""; }} />
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy} data-testid="admin-lexicon-import" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-bone/70 border border-white/15 px-3 py-1.5 hover:text-gold hover:border-gold/40">
              <Upload size={12} strokeWidth={1.75} /> {t("admin.lexicon.action.import")}
            </button>
          </div>
        </header>

        {/* Counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-lexicon-stats">
          <StatCard testId="lex-stat-total"    label={t("admin.lexicon.stat.total")}   value={counts.total} />
          <StatCard testId="lex-stat-enabled"  label={t("admin.lexicon.stat.enabled")} value={counts.enabled} />
          <StatCard testId="lex-stat-brand"    label={t("admin.lexicon.stat.brand")}    value={counts.by_category?.brand || 0} />
          <StatCard testId="lex-stat-philo"    label={t("admin.lexicon.stat.philosophy")} value={counts.by_category?.philosophy || 0} />
        </div>

        {/* Test translation playground */}
        <LexiconTestTranslation />

        {/* Filter row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex border border-white/15 divide-x divide-white/10" data-testid="admin-lexicon-tabs">
            {[{ key: "all", label: t("admin.lexicon.tab.all") }, ...CATEGORY_OPTIONS.map((c) => ({ key: c.key, label: t(`admin.lexicon.cat.${c.key}`) }))].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setCategory(tab.key)}
                data-testid={`admin-lexicon-tab-${tab.key}`}
                className={`px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase transition-colors ${
                  category === tab.key ? "bg-gold/10 text-gold" : "text-bone/60 hover:text-bone"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-[360px]">
            <Search size={13} strokeWidth={1.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-bone/50" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.lexicon.searchPlaceholder")}
              data-testid="admin-lexicon-search"
              className="w-full bg-black border border-white/15 text-bone pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-gold"
            />
          </div>
        </div>

        {/* Flash msg / error */}
        {msg && <p className="text-sm text-emerald-400" data-testid="admin-lexicon-msg">{msg}</p>}
        {err && <p className="text-sm text-red-400" data-testid="admin-lexicon-err">{err}</p>}

        {/* Table */}
        <div className="border border-gold/15 bg-white/[0.02]" data-testid="admin-lexicon-table">
          {loading ? (
            <p className="p-6 text-sm text-bone/50 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> {t("admin.label.loading")}</p>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-sm text-bone/50 text-center">{t("admin.lexicon.empty")}</p>
          ) : (
            <div className="divide-y divide-white/5">
              {filtered.map((row) => (
                <div key={row.id} data-testid={`admin-lexicon-row-${row.id}`} className="px-4 md:px-5 py-3 flex items-center gap-3 flex-wrap md:flex-nowrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <p className={`font-serif text-[15px] truncate ${row.enabled ? "text-bone" : "text-bone/40 line-through"}`}>
                        {row.term}
                      </p>
                      <CategoryPill category={row.category} />
                      {!row.enabled && <span className="text-[9px] tracking-[0.28em] uppercase text-bone/40 border border-white/15 px-1.5 py-0.5">{t("admin.lexicon.disabled")}</span>}
                    </div>
                    {(row.aliases?.length || row.notes) && (
                      <p className="mt-1 text-[11px] text-bone/55 truncate">
                        {row.aliases?.length ? `${t("admin.lexicon.aliases")}: ${row.aliases.join(", ")}` : ""}
                        {row.aliases?.length && row.notes ? " · " : ""}
                        {row.notes ? row.notes : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button type="button" onClick={() => toggle(row)} disabled={busy} data-testid={`admin-lexicon-toggle-${row.id}`} title={row.enabled ? t("admin.lexicon.action.disable") : t("admin.lexicon.action.enable")} className={`p-2 border border-transparent hover:border-gold/30 ${row.enabled ? "text-emerald-400" : "text-bone/50"}`}>
                      {row.enabled ? <ToggleRight size={16} strokeWidth={1.5} /> : <ToggleLeft size={16} strokeWidth={1.5} />}
                    </button>
                    <button type="button" onClick={() => openEdit(row)} data-testid={`admin-lexicon-edit-${row.id}`} title={t("admin.action.edit")} className="p-2 text-bone/70 hover:text-gold border border-transparent hover:border-gold/30">
                      <Edit3 size={14} strokeWidth={1.5} />
                    </button>
                    <button type="button" onClick={() => remove(row)} disabled={busy} data-testid={`admin-lexicon-delete-${row.id}`} title={t("admin.action.delete")} className="p-2 text-bone/70 hover:text-red-400 border border-transparent hover:border-red-400/30">
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer helper */}
        <div className="border border-white/8 bg-white/[0.02] p-4 flex items-start gap-3" data-testid="admin-lexicon-help">
          <ShieldCheck size={16} strokeWidth={1.5} className="text-gold flex-shrink-0 mt-0.5" />
          <p className="text-sm text-bone/70 leading-relaxed">
            {t("admin.lexicon.help")}
          </p>
        </div>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeEditor} data-testid="admin-lexicon-editor">
          <div className="bg-ink border border-gold/40 max-w-lg w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-gold">
                  {editing.id ? t("admin.lexicon.editor.edit") : t("admin.lexicon.editor.new")}
                </p>
                <h3 className="font-serif text-xl text-bone mt-1">{t("admin.lexicon.title")}</h3>
              </div>
              <button type="button" onClick={closeEditor} className="p-2 text-bone/60 hover:text-bone"><X size={18} strokeWidth={1.5} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.lexicon.field.term")} *</label>
                <input
                  type="text"
                  autoFocus
                  value={editing.term}
                  onChange={(e) => setEditing({ ...editing, term: e.target.value })}
                  data-testid="admin-lexicon-input-term"
                  placeholder="e.g. Virtue Guide"
                  className="w-full bg-black border border-gold/30 text-bone px-3 py-2.5 focus:outline-none focus:border-gold"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.lexicon.field.category")}</label>
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  data-testid="admin-lexicon-input-category"
                  className="w-full bg-black border border-gold/30 text-bone px-3 py-2.5 focus:outline-none focus:border-gold"
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.key} value={c.key}>{t(`admin.lexicon.cat.${c.key}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.lexicon.field.aliases")}</label>
                <input
                  type="text"
                  value={editing.aliases}
                  onChange={(e) => setEditing({ ...editing, aliases: e.target.value })}
                  data-testid="admin-lexicon-input-aliases"
                  placeholder="Alt spellings, comma-separated"
                  className="w-full bg-black border border-gold/30 text-bone px-3 py-2.5 focus:outline-none focus:border-gold"
                />
                <p className="mt-1 text-[11px] text-bone/50">{t("admin.lexicon.field.aliasesHint")}</p>
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.lexicon.field.notes")}</label>
                <textarea
                  value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  data-testid="admin-lexicon-input-notes"
                  rows={2}
                  className="w-full bg-black border border-gold/30 text-bone px-3 py-2.5 focus:outline-none focus:border-gold resize-y"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-bone/85 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  data-testid="admin-lexicon-input-enabled"
                  className="w-4 h-4 accent-gold"
                />
                {t("admin.lexicon.field.enabled")}
              </label>
            </div>
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
              <button type="button" onClick={closeEditor} className="text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/20 px-3 py-1.5 hover:text-bone">{t("admin.action.cancel")}</button>
              <button
                type="button"
                onClick={save}
                disabled={busy || !editing.term.trim()}
                data-testid="admin-lexicon-save"
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/60 px-3 py-1.5 hover:bg-gold/10 disabled:opacity-40"
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} strokeWidth={1.75} />}
                {t("admin.action.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
